import React, { useState, useEffect } from 'react';
import './HomeyTable.css';
import { capitalizeFirstLetter } from './util/Util';
import AccessRequestModal from './components/AccessRequestModal';

const HomeyTable = ({ homeys }) => {
    const [countdowns, setCountdowns] = useState({});
    const [sortBy, setSortBy] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    const [showModal, setShowModal] = useState(false);
    const [selectedHomey, setSelectedHomey] = useState(null);

    useEffect(() => {
        const interval = setInterval(() => {
            const newCountdowns = {};
            homeys.forEach(homey => {
                const installerUser = homey.users.find(user => user.role === 'installer');
                if (installerUser?.enabled && installerUser?.enabledUntil) {
                    const diff = new Date(installerUser.enabledUntil) - new Date();
                    if (diff > 0) {
                        const hours = String(Math.floor(diff / 3600000)).padStart(2, '0');
                        const minutes = String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0');
                        const seconds = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
                        newCountdowns[homey.id] = `${hours}:${minutes}:${seconds}`;
                    }
                }
            });
            setCountdowns(newCountdowns);
        }, 1000);

        return () => clearInterval(interval);
    }, [homeys]);

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortDirection('asc');
        }
    };

    function compareVersions(a, b) {
        const splitA = (a || '').split('.').map(Number);
        const splitB = (b || '').split('.').map(Number);

        const maxLength = Math.max(splitA.length, splitB.length);
        for (let i = 0; i < maxLength; i++) {
            const numA = splitA[i] || 0;
            const numB = splitB[i] || 0;
            if (numA > numB) return 1;
            if (numA < numB) return -1;
        }
        return 0;
    }

    const sortedHomeys = [...homeys].sort((a, b) => {
        let aValue, bValue;
        switch (sortBy) {
            case 'name':
                aValue = a.name?.toLowerCase() || '';
                bValue = b.name?.toLowerCase() || '';
                break;
            case 'version':
                const result = compareVersions(a.softwareVersion, b.softwareVersion);
                return sortDirection === 'asc' ? result : -result;
            case 'status':
                aValue = a.state || '';
                bValue = b.state || '';
                break;
            default:
                aValue = '';
                bValue = '';
        }
        if (sortBy !== 'version') {
            if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        }
        return 0;
    });

    const renderSortIcon = (column) => {
        if (sortBy === column) {
            return <img
                src="/single_sort_button.svg"
                alt={sortDirection === 'asc' ? 'Sort ascending' : 'Sort descending'}
                style={{
                    width: "12px",
                    verticalAlign: 'middle',
                    transition: 'transform 0.15s',
                    transform: sortDirection === 'desc' ? 'rotate(180deg)' : 'none',
                }}
            />
        }
        return <img src="/single_sort_button.svg" alt="Sort" style={{
            width: '12px',
            verticalAlign: 'middle',
            opacity: 0,
        }} />;
    };

    const requestAccess = async (homey) => {
        setSelectedHomey(homey);
        setShowModal(true);
    };

    return <>
        {homeys === null && (
            <div className="CenteredMessageWrapper">
                <div className="CenteredMessage">
                    <p>Loading...</p>
                </div>
            </div>
        )}

        {Array.isArray(homeys) && homeys.length === 0 && (
            <div className="CenteredMessageWrapper">
                <div className="CenteredMessage">
                    <p>There are no Homey Pros under your management yet.
                    </p>
                    <p>Install your first Homey Pro for a customer, and it will show up here.
                    </p>
                </div>
            </div>
        )}
            
        {Array.isArray(homeys) && homeys.length > 0 && (
            <div className="HomeyTableWrapper">
                <table className="HomeyTable">
                    <thead>
                        <tr>
                            <th />
                            <th className="name" onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>
                                Name&nbsp;{renderSortIcon('name')}
                            </th>
                            <th className="version" onClick={() => handleSort('version')} style={{ cursor: 'pointer' }}>
                                Version&nbsp;{renderSortIcon('version')}
                            </th>
                            <th className="status" onClick={() => handleSort('status')} style={{ cursor: 'pointer' }}>
                                Status&nbsp;{renderSortIcon('status')}
                            </th>

                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {sortedHomeys.map((homey, index) => {
                            const isOnline = homey?.state === 'online';
                            const isConnecting = homey?.state === 'connecting';
                            const dotColor = isOnline
                                ? 'var(--color-green-600)'
                                : isConnecting
                                    ? 'var(--color-orange)'
                                    : 'var(--color-red-500)';
                            const installerUser = homey.users.find(user => user.role === 'installer');
                            const isEnabled = installerUser?.enabled && new Date(installerUser.enabledUntil) > new Date();

                            return (
                                <tr key={homey?.id} className={index % 2 === 0 ? 'row-grey' : 'row-white'}>
                                    <td className="HomeyImageWrapper">
                                        <img
                                            className="HomeyImage"
                                            src={homey?.modelImage?.light}
                                            alt={homey?.modelName || homey?.model}
                                        />
                                    </td>
                                    <td className="name">{homey?.name}</td>
                                    <td className="version">v{homey?.softwareVersion}</td>
                                    <td className="status">
                                        <span
                                            style={{
                                                marginRight: '8px',
                                                width: '10px',
                                                height: '10px',
                                                borderRadius: '50%',
                                                backgroundColor: dotColor,
                                                display: 'inline-block',
                                            }}
                                        />
                                        <span style={{ color: dotColor }}>
                                            {capitalizeFirstLetter(homey?.state)}{homey?.state === 'connecting' ? '...' : ''}
                                        </span>
                                    </td>
                                    <td className="HomeyActionWrapper">
                                        {isEnabled ? (
                                            <>
                                                <p className="HomeyActionTimer">Access expires in {countdowns[homey.id] || '00:00:00'}</p>
                                                <a
                                                    className="HomeyActionAsParagraph"
                                                    href={`https://my.homey.app/homeys/${homey.id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Open Web App
                                                </a>
                                                <img src="external_link.png" alt="External Link" className="ExternalLinkIcon" />

                                            </>
                                        ) : (
                                            <p className="HomeyAction" onClick={() => requestAccess(homey)}>
                                                Request Access &raquo;
                                            </p>
                                        )}
                                    </td>
                                </tr>
                            );
                        })
                        }
                    </tbody>
                </table>
            </div>
        )}

        <AccessRequestModal
            open={showModal}
            onClose={() => setShowModal(false)}
            homey={selectedHomey}
        />
    </>
};

export default HomeyTable;
